import { Link } from "react-router";

export interface DuviNicheSiteBrandMarkProps {
  size?: number;
  showWordmark?: boolean;
  /** `light` sits on canvas (nav); `dark` sits on the footer's ink band. */
  tone?: "light" | "dark";
  /** Same-site route the mark links to. Omit to render a non-linked mark. */
  to?: string;
  className?: string;
}

/**
 * DuviNicheSiteBrandMark — the Fishwalkers lock-up, ported from the bespoke
 * design system's `components/core/BrandMark.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip).
 *
 * The mark is plain markup on purpose rather than an SVG asset: transactional
 * email rebuilds it with a table cell and a letter, no image required, so nav,
 * footer and email share one lock-up.
 *
 * The export's `href` is a router `to` here — the mark always points at this
 * site's own home route.
 */
export function DuviNicheSiteBrandMark({
  size = 36,
  showWordmark = true,
  tone = "light",
  to,
  className = "",
}: DuviNicheSiteBrandMarkProps) {
  const wordInk =
    tone === "dark"
      ? "text-[color:var(--brand-foreground)]"
      : "text-[color:var(--heading)]";
  const disc =
    tone === "dark"
      ? "bg-[var(--brand-foreground)] text-[color:var(--brand)]"
      : "bg-[var(--brand)] text-[color:var(--brand-foreground)]";

  const inner = (
    <>
      <span
        aria-hidden="true"
        className={[
          "inline-flex shrink-0 items-center justify-center rounded-full font-[family-name:var(--font-display)] font-extrabold leading-none",
          disc,
        ].join(" ")}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.56) }}
      >
        F
      </span>
      {showWordmark && (
        <span
          className={[
            "font-[family-name:var(--font-display)] font-extrabold tracking-[-0.015em]",
            wordInk,
          ].join(" ")}
          style={{ fontSize: Math.round(size * 0.62) }}
        >
          Fishwalkers
        </span>
      )}
    </>
  );

  const classes = [
    "inline-flex items-center gap-[10px] no-underline hover:no-underline",
    className,
  ].join(" ");

  return to ? (
    <Link to={to} className={classes} aria-label="Fishwalkers home">
      {inner}
    </Link>
  ) : (
    <span className={classes}>{inner}</span>
  );
}
